import { getAccessToken } from '../lib/authSession';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const CURSOR_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#6366F1'
];

// Polling interval in milliseconds
const POLL_INTERVAL = 1000;
const CURSOR_UPDATE_INTERVAL = 100;

const CollaborativeCursors = ({ 
  contractId, 
  containerRef,
  editorRef,
  onRemoteEdit
}) => {
  const { user } = useAuth();
  const [remoteCursors, setRemoteCursors] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [connectionType, setConnectionType] = useState(null); // 'websocket' or 'polling'
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const pendingCursorRef = useRef(null);
  const cursorThrottleRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getColorForUser = useCallback((userId) => {
    const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return CURSOR_COLORS[hash % CURSOR_COLORS.length];
  }, []);

  // ==================== POLLING METHODS ====================
  
  const startPolling = useCallback(async () => {
    if (!contractId || !user) {
      console.log('startPolling: Missing contractId or user, skipping');
      return;
    }

    const token = getAccessToken();
    if (!token) {
      console.log('startPolling: No token available');
      return;
    }

    console.log('startPolling: Attempting to join collaboration via HTTP');
    
    try {
      // Join collaboration session
      const joinRes = await fetch(`${API_URL}/api/contracts/${contractId}/collaboration/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!joinRes.ok) {
        console.error('Failed to join collaboration session:', joinRes.status);
        return;
      }

      const joinData = await joinRes.json();
      console.log('startPolling: Successfully joined, starting poll interval');
      setIsConnected(true);
      setConnectionType('polling');
      
      // Initialize with active users
      if (joinData.active_users) {
        const cursors = {};
        joinData.active_users.forEach(u => {
          if (u.user_id !== user?.id) {
            cursors[u.user_id] = {
              ...u,
              lastUpdate: Date.now()
            };
          }
        });
        setRemoteCursors(cursors);
      }

      // Start polling loop
      pollIntervalRef.current = setInterval(async () => {
        try {
          const pollRes = await fetch(
            `${API_URL}/api/contracts/${contractId}/collaboration/poll${lastTimestampRef.current ? `?last_timestamp=${encodeURIComponent(lastTimestampRef.current)}` : ''}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (pollRes.ok) {
            const data = await pollRes.json();
            lastTimestampRef.current = data.timestamp;

            // Update cursors
            if (data.updates?.cursors) {
              setRemoteCursors(prev => {
                const updated = { ...prev };
                data.updates.cursors.forEach(cursor => {
                  if (cursor.user_id !== user?.id) {
                    updated[cursor.user_id] = {
                      ...cursor,
                      lastUpdate: Date.now()
                    };
                  }
                });
                return updated;
              });
            }

            // Handle edits
            if (data.updates?.edits && onRemoteEdit) {
              data.updates.edits.forEach(edit => {
                if (edit.user_id !== user?.id) {
                  onRemoteEdit(edit.data);
                }
              });
            }

            // Update active users list
            if (data.active_users) {
              setRemoteCursors(prev => {
                const activeIds = new Set(data.active_users.map(u => u.user_id));
                const updated = {};
                // Keep only active users
                Object.keys(prev).forEach(uid => {
                  if (activeIds.has(uid) && uid !== user?.id) {
                    updated[uid] = prev[uid];
                  }
                });
                // Add new users
                data.active_users.forEach(u => {
                  if (u.user_id !== user?.id && !updated[u.user_id]) {
                    updated[u.user_id] = {
                      ...u,
                      lastUpdate: Date.now()
                    };
                  }
                });
                return updated;
              });
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, POLL_INTERVAL);

    } catch (err) {
      console.error('Failed to start polling:', err);
    }
  }, [contractId, user, API_URL, onRemoteEdit]);

  const stopPolling = useCallback(async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    const token = getAccessToken();
    if (token && contractId) {
      try {
        await fetch(`${API_URL}/api/contracts/${contractId}/collaboration/leave`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        // Ignore cleanup errors
      }
    }
  }, [contractId, API_URL]);

  // ==================== WEBSOCKET METHODS ====================

  const connectWebSocket = useCallback(() => {
    if (!contractId || !user) {
      console.log('connectWebSocket: Missing contractId or user');
      return;
    }

    const token = getAccessToken();
    if (!token) {
      console.log('connectWebSocket: No token, will retry when token becomes available');
      return;
    }

    console.log('connectWebSocket: Attempting WebSocket connection...');
    const wsUrl = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    console.log('connectWebSocket: WS URL:', `${wsUrl}/ws/collaborate/${contractId}`);
    
    const ws = new WebSocket(`${wsUrl}/ws/collaborate/${contractId}`);
    
    // Track if we've already triggered fallback to polling
    let hasTriggeredFallback = false;
    
    // Set a timeout for WebSocket connection
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN && !hasTriggeredFallback) {
        console.log('WebSocket connection timeout, falling back to polling');
        hasTriggeredFallback = true;
        try {
          ws.close();
        } catch (e) {
          console.log('Error closing WebSocket:', e);
        }
        startPolling();
      }
    }, 5000);

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      // Send auth token as first message
      ws.send(JSON.stringify({ token }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'connected':
            setIsConnected(true);
            setConnectionType('websocket');
            // Start ping interval
            pingIntervalRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, 30000);
            break;
            
          case 'cursor':
            if (data.user_id !== user?.id) {
              setRemoteCursors(prev => ({
                ...prev,
                [data.user_id]: {
                  ...data.cursor,
                  lastUpdate: Date.now()
                }
              }));
            }
            break;
            
          case 'presence':
            if (data.action === 'left') {
              setRemoteCursors(prev => {
                const updated = { ...prev };
                delete updated[data.user_id];
                return updated;
              });
            } else if (data.action === 'joined' && data.active_users) {
              const cursors = {};
              data.active_users.forEach(u => {
                if (u.user_id !== user?.id) {
                  cursors[u.user_id] = {
                    ...u,
                    lastUpdate: Date.now()
                  };
                }
              });
              setRemoteCursors(cursors);
            }
            break;
            
          case 'edit':
            if (data.user_id !== user?.id && onRemoteEdit) {
              onRemoteEdit(data.data);
            }
            break;
            
          case 'error':
            console.error('WebSocket error:', data.error);
            break;
            
          default:
            break;
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };

    ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      setIsConnected(false);
      setConnectionType(null);
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      
      // If WebSocket failed and we haven't already started polling, try polling instead
      if (event.code !== 1000 && !hasTriggeredFallback) {
        console.log('WebSocket closed unexpectedly, switching to polling');
        hasTriggeredFallback = true;
        startPolling();
      } else if (event.code === 1000 && !hasTriggeredFallback) {
        // Normal close, try to reconnect with WebSocket
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      clearTimeout(connectionTimeout);
      // Don't close here, let onclose handle the fallback
    };

    wsRef.current = ws;
  }, [contractId, user, API_URL, onRemoteEdit, startPolling]);

  // ==================== INITIALIZATION ====================

  useEffect(() => {
    console.log('CollaborativeCursors: Initializing with contractId:', contractId, 'user:', user?.id);
    // Try WebSocket first, fallback to polling
    connectWebSocket();

    return () => {
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      stopPolling();
    };
  }, [connectWebSocket, stopPolling]);

  // Clean up stale cursors
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setRemoteCursors(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(userId => {
          if (now - updated[userId].lastUpdate > 10000) {
            delete updated[userId];
          }
        });
        return updated;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // ==================== CURSOR & EDIT SENDING ====================

  const sendCursorPosition = useCallback((position, selection = null) => {
    const token = getAccessToken();
    
    if (connectionType === 'websocket' && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor',
        position,
        selection
      }));
    } else if (connectionType === 'polling' && token) {
      // Throttle cursor updates for polling
      pendingCursorRef.current = { position, selection };
      
      if (!cursorThrottleRef.current) {
        cursorThrottleRef.current = setTimeout(async () => {
          if (pendingCursorRef.current) {
            try {
              await fetch(`${API_URL}/api/contracts/${contractId}/collaboration/cursor`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(pendingCursorRef.current)
              });
            } catch (err) {
              console.error('Failed to send cursor:', err);
            }
          }
          cursorThrottleRef.current = null;
        }, CURSOR_UPDATE_INTERVAL);
      }
    }
  }, [connectionType, contractId, API_URL]);

  const sendEdit = useCallback((editData) => {
    const token = getAccessToken();
    
    if (connectionType === 'websocket' && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'edit',
        data: editData
      }));
    } else if (connectionType === 'polling' && token) {
      fetch(`${API_URL}/api/contracts/${contractId}/collaboration/edit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ data: editData })
      }).catch(err => console.error('Failed to send edit:', err));
    }
  }, [connectionType, contractId, API_URL]);

  // Track cursor movement in container
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    let lastPosition = null;
    let throttleTimeout = null;

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const position = {
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100
      };

      // Throttle updates
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          if (JSON.stringify(position) !== JSON.stringify(lastPosition)) {
            lastPosition = position;
            sendCursorPosition(position);
          }
          throttleTimeout = null;
        }, 50);
      }
    };

    const handleSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (!range.collapsed && container.contains(range.commonAncestorContainer)) {
          sendCursorPosition(lastPosition, {
            start: range.startOffset,
            end: range.endOffset
          });
        }
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('selectionchange', handleSelection);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('selectionchange', handleSelection);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [containerRef, sendCursorPosition]);

  // Track text input changes
  useEffect(() => {
    const editor = editorRef?.current;
    if (!editor) return;

    const handleInput = (e) => {
      sendEdit({
        type: 'input',
        value: e.target.value,
        cursorPosition: e.target.selectionStart
      });
    };

    editor.addEventListener('input', handleInput);
    return () => editor.removeEventListener('input', handleInput);
  }, [editorRef, sendEdit]);

  return (
    <>
      {/* Connection indicator */}
      <div 
        data-testid="collaboration-status"
        className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
          isConnected 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
        }`}
      >
        <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'}`} />
        {isConnected ? (
          <>
            Colaboración activa
            <span className="opacity-60">({connectionType === 'websocket' ? 'WS' : 'HTTP'})</span>
          </>
        ) : (
          'Conectando...'
        )}
        {Object.keys(remoteCursors).length > 0 && (
          <span className="ml-1">
            · {Object.keys(remoteCursors).length} {Object.keys(remoteCursors).length === 1 ? 'usuario' : 'usuarios'}
          </span>
        )}
      </div>

      {/* Remote cursors */}
      {containerRef?.current && Object.entries(remoteCursors).map(([userId, cursor]) => {
        if (!cursor.cursor_position) return null;
        
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const x = (cursor.cursor_position.x / 100) * rect.width;
        const y = (cursor.cursor_position.y / 100) * rect.height;
        const color = cursor.color || getColorForUser(userId);

        return (
          <div
            key={userId}
            data-testid={`remote-cursor-${userId}`}
            className="pointer-events-none fixed z-40 transition-all duration-75"
            style={{
              left: rect.left + x,
              top: rect.top + y,
              transform: 'translate(-2px, -2px)'
            }}
          >
            {/* Cursor pointer */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
            >
              <path
                d="M5.5 3.5L19.5 12.5L12.5 14.5L9.5 21.5L5.5 3.5Z"
                fill={color}
                stroke="white"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
            
            {/* Username label */}
            <div
              className="absolute left-5 top-4 px-2 py-0.5 rounded text-xs font-medium text-white whitespace-nowrap"
              style={{ backgroundColor: color }}
            >
              {cursor.username || 'Usuario'}
            </div>
          </div>
        );
      })}
    </>
  );
};

// Hook for using collaborative editing
export const useCollaborativeEditing = (contractId) => {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const [remoteChanges, setRemoteChanges] = useState(null);

  const handleRemoteEdit = useCallback((editData) => {
    setRemoteChanges(editData);
  }, []);

  return {
    containerRef,
    editorRef,
    remoteChanges,
    CursorsComponent: (
      <CollaborativeCursors
        contractId={contractId}
        containerRef={containerRef}
        editorRef={editorRef}
        onRemoteEdit={handleRemoteEdit}
      />
    )
  };
};

export default CollaborativeCursors;
